import { orient } from '../../boardLayout';

// A tiny drawing of the board: filled squares for the painted canvases,
// dashed ones for the spots that would still need a canvas.
function MiniBoard({ cols, rows, spare }) {
  const box = 76;
  const unit = Math.min(box / cols, (box * 0.78) / rows);
  const gap = unit * 0.16;
  const width = cols * unit;
  const height = rows * unit;
  const total = cols * rows;

  return (
    <svg className="of-mini" width={width} height={height} viewBox={`0 0 ${width} ${height}`} aria-hidden="true">
      {Array.from({ length: total }, (_, i) => {
        const empty = i >= total - spare;
        return (
          <rect
            key={i}
            x={(i % cols) * unit + gap / 2}
            y={Math.floor(i / cols) * unit + gap / 2}
            width={unit - gap}
            height={unit - gap}
            className={empty ? 'of-mini-empty' : 'of-mini-full'}
          />
        );
      })}
    </svg>
  );
}

// The board shapes that fit the painter count (from boardLayout.js), each
// with its size in cm, what the board costs and how many canvases it would
// still need to be full - plus landscape/portrait for any shape that isn't
// square. The price follows the sheet the board is cut from, so a bigger
// shape can cost more.
function LayoutPicker({ options, selected, sizeCm, formatMoney, orientation, onSelect, onOrientation, t }) {
  const isSquare = selected.cols === selected.rows;
  const spareText = (spare) => (spare === 0 ? t('order.board.shape.full') : t('order.board.shape.spare', { count: spare }));
  // The board is the same size whichever way it hangs.
  const sizeText = (option) => `${option.cols * sizeCm} × ${option.rows * sizeCm} cm`;
  // No sheet while there is no shape to show (a count between two shapes).
  const price = (option) => (option.sheet ? formatMoney(option.sheet.price_cents) : '');

  return (
    <>
      {options.length > 1 ? (
        <div className="of-layouts" role="radiogroup">
          {options.map((option) => {
            const shown = orient(option, option.cols === option.rows ? 'landscape' : orientation);
            const active = option.cols === selected.cols && option.rows === selected.rows;
            return (
              <button
                key={`${option.cols}x${option.rows}`}
                type="button"
                role="radio"
                aria-checked={active}
                className={`of-layout ${active ? 'is-selected' : ''}`}
                onClick={() => onSelect(option)}
              >
                <MiniBoard {...shown} />
                <span className="of-layout-name">{sizeText(shown)}</span>
                <span className="of-layout-price">{price(option)}</span>
                <span className={`of-layout-spare ${option.spare === 0 ? 'is-full' : ''}`}>
                  {spareText(option.spare)}
                </span>
              </button>
            );
          })}
        </div>
      ) : (
        <div className="of-boardcard">
          <MiniBoard {...orient(selected, isSquare ? 'landscape' : orientation)} />
          <div className="of-boardcard-main">
            <strong>{sizeText(orient(selected, isSquare ? 'landscape' : orientation))}</strong>
            <span className={`of-layout-spare ${selected.spare === 0 ? 'is-full' : ''}`}>
              {spareText(selected.spare)}
            </span>
          </div>
          <span className="of-boardcard-price">{price(selected)}</span>
        </div>
      )}

      {!isSquare && (
        <div className="of-seg" role="radiogroup" aria-label={t('order.board.shape.orientation')}>
          {['landscape', 'portrait'].map((id) => (
            <button
              key={id}
              type="button"
              role="radio"
              aria-checked={orientation === id}
              className={`of-seg-option ${orientation === id ? 'is-selected' : ''}`}
              onClick={() => onOrientation(id)}
            >
              <span className={`of-seg-icon of-seg-icon--${id}`} aria-hidden="true" />
              {t(`order.board.shape.${id}`)}
            </button>
          ))}
        </div>
      )}
    </>
  );
}

export default LayoutPicker;
